import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { RealEstateProject } from '../models/real-estate-project.model';
import { UploadFile } from '../models/upload-file.model';
import { DataService } from '../services/data.service';
import { ErrorDialogComponent } from '../error-dialog/error-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { RealEstateService } from '../services/real-estate.service';
import { RealEstateFormErrorDialogComponent } from './common/real-estate-form-error-dialog/real-estate-form-error-dialog.component';
import { RealEstateAssignmentDialogComponent } from './real-estate-assignment-dialog/real-estate-assignment-dialog.component';
import { GraphUser } from '../models/graph-user.model';
import { DeSnackbarData } from '../shared/components/de-snackbar/de-snackbar-config';
import { DeSnackbarService } from '../shared/components/de-snackbar/de-snackbar.service';
import { parseDateTime } from '../shared/utils/utils';
import { FormApprovalDialogComponent } from '../form-approval-dialog/form-approval-dialog.component';
import { ConfirmDeleteFormDialogComponent } from '../confirm-delete-form-dialog/confirm-delete-form-dialog.component';
import { RREFormStatus } from '../real-estate-form-dashboard/common/real-estate-form-dashboard.types';

@Component({
  selector: 'app-real-estate-project',
  templateUrl: './real-estate-project.component.html',
  styleUrls: ['./real-estate-project.component.scss'],
  standalone: false,
})
export class RealEstateProjectComponent implements OnInit {
  realEstateProjectForm!: FormGroup; // Parent form
  realEstateTitleForm!: FormGroup; // Form received from the real-estate-project-title component
  realEstateDetailForm!: FormGroup; // Form received from the real-estate-project-detail-info component
  realEstateJustificationForm!: FormGroup; // Form received from the real-estate-project-justification component
  realEstateUopForm!: FormGroup; // Form received from the real-estate-project-uop component
  realEstateEstimateForm!: FormGroup; // Form received from the real-estate-project-estimate component
  realEstateWorkflow = [];
  realEstateProject: RealEstateProject;
  landServicesDetailForm!: FormGroup; // Form received from the land-services-project-detail-info component
  landServicesEstimateDetailsForm!: FormGroup; // Form received from the land-services-project-estimate-details component
  landServicesAccountingForm!: FormGroup; // Form received from the land-services-accounting-codeblock component
  approverList = [];
  doaAmounts: number[] = [];
  formOwner: GraphUser;
  formOwnerFullDetails: GraphUser;
  currentAssignment: GraphUser;
  initializingLandServices = false;
  // Attachments inputs
  reviewMode = false; // Track if form is in review mode
  isReadyForDOAApproval = false;
  isReadyForITSpareApproval = false;
  files: UploadFile[] = [];
  // Persistable attachment descriptors (keeps UI UploadFile unchanged)
  filesCopy: {
    fileName: string;
    contentType: string;
    uploadedBy?: string;
    uploadedDate?: string;
    storageFilePath?: string;
  }[] = [];

  // Query Params
  pId: string;
  projectData: any;
  formStatus: RREFormStatus;
  userEmail = '';
  userName = '';
  projectId = '';
  formActivityVisible = false;
  formActivity: any[];
  showDerivationError: boolean;
  reviewComments: string;
  changeArea: 'change_in_scope' | 'estimate' | 'both' | null = null;
  disableEstimateSection = false;
  disableDetailSection = false;
  hideActivityCTA = true;
  readyToAddAccountingCodeBlock = false;
  emailRecipients: any;
  private httpNoInterceptors: HttpClient;

  constructor(
    private fb: FormBuilder,
    private dataService: DataService,
    private dialog: MatDialog,
    private http: HttpClient,
    private realEstateService: RealEstateService,
    private route: ActivatedRoute,
    private router: Router,
    private snackService: DeSnackbarService,
    httpBackend: HttpBackend
  ) {
    this.httpNoInterceptors = new HttpClient(httpBackend);
    this.realEstateProject = new RealEstateProject();

    const userData = JSON.parse(sessionStorage.getItem('userInfo'));
    if (userData && userData.account) {
      this.userEmail = userData.account.username;
      this.userName = userData.account.name;
      this.getUserDetails(userData.account.name);
    }
  }

  //Current values of funding
  private baselineCurrentFunding: Record<string, number> | null = null;
  private baselinePreviousFunding: Record<string, number> | null = null;

  private readonly fundingMappings = [
    { current: 'currentEstCapCost', previous: 'previousEstCapCost', total: 'totalEstCapCost' },
    { current: 'currentEstAFUDC', previous: 'previousEstAFUDC', total: 'totalEstAFUDC' },
    {
      current: 'currentTotalCapital',
      previous: 'previousTotalCapital',
      total: 'totalTotalCapital',
    },
    { current: 'currentEstOM', previous: 'previousEstOM', total: 'totalEstOM' },
    {
      current: 'currentTotalNoAFUDC',
      previous: 'previousTotalNoAFUDC',
      total: 'totalTotalNoAFUDC',
    },
    {
      current: 'currentTotalWithAFUDC',
      previous: 'previousTotalWithAFUDC',
      total: 'totalTotalWithAFUDC',
    },
  ];

  ngOnInit() {
    this.realEstateTitleForm = this.fb.group({
      realEstateDepartment: [''],
      fundingType: [''],
      fm: [false],
      pm: [false],
      myrp: [false],
      region: [''],
      acquisition: [false],
      land: [false],
      disposal: [false],
      building: [false],
      estimate: [false],
      projectDate: [false],
      changeInScope: [false],
      capitalExpenditure: [false],
      omExpenditure: [false],
    });

    this.realEstateDetailForm = this.fb.group({
      updateType: [''],
      updateRevision: [''],
      state: [''],
      department: [''],
      adaptiveId: ['', [Validators.minLength(12), Validators.maxLength(12)]],
      projectId: [''],
      building: [''],
      assetLocation: [''],
      majorLocation: [''],
      city: [''],
      businessUnit: [''],
      fundingProject: [''],
      projectType: [''],
      projectClass: [''],
      projectDescription: [''],
      projectManager: ['', [Validators.required]],
      sponsor: [''],
      contractAdmin: [''],
      assetContact: [''],
      additionalContact: [''],
      startDate: [''],
      serviceDate: [''],
      completionDate: [''],
      actualInServiceDate: [{ value: '', disabled: true }],
      omOperatingUnits: [''],
      capitalOperatingUnits: [''],
      omAccountNum: [''],
      capitalCwipAccountNum: [''],
      capitalRwipAccountNum: [''],
      assetLocationId: [''],
      majorLocationId: [''],
      isLocationLeased: [false],
      leaseStartDate: [null],
      leaseEndDate: [null],
    });

    this.realEstateJustificationForm = this.fb.group({
      projectJustification: [''],
      projectReason: this.fb.array([]),
    });

    this.realEstateUopForm = this.fb.group({
      uopDetails: this.fb.array([]),
    });

    this.realEstateEstimateForm = this.fb.group({
      estimates: this.fb.array([]),
      chargeSummary: this.fb.group({
        laborCapitalAdditions: ['0'],
        laborCostOfRemoval: ['0'],
        laborOM: ['0'],
        laborTotal: ['0'],
        contractorCapitalAdditions: ['0'],
        contractorCostOfRemoval: ['0'],
        contractorOM: ['0'],
        contractorTotal: ['0'],
        materialCapitalAdditions: ['0'],
        materialCostOfRemoval: ['0'],
        materialOM: ['0'],
        materialTotal: ['0'],
        otherCapitalAdditions: ['0'],
        otherCostOfRemoval: ['0'],
        otherOM: ['0'],
        otherTotal: ['0'],
        approvedCapitalAdditions: ['0'],
        approvedCostOfRemoval: ['0'],
        approvedOM: ['0'],
        approvedTotal: ['0'],
      }),
      expenditureSummary: this.fb.group({
        previousEstCapCost: ['0'],
        previousEstAFUDC: ['0'],
        previousTotalCapital: ['0'],
        previousEstOM: ['0'],
        previousTotalNoAFUDC: ['0'],
        previousTotalWithAFUDC: ['0'],
        currentEstCapCost: ['0'],
        currentEstAFUDC: ['0'],
        currentTotalCapital: ['0'],
        currentEstOM: ['0'],
        currentTotalNoAFUDC: ['0'],
        currentTotalWithAFUDC: ['0'],
        totalEstCapCost: ['0'],
        totalEstAFUDC: ['0'],
        totalTotalCapital: ['0'],
        totalEstOM: ['0'],
        totalTotalNoAFUDC: ['0'],
        totalTotalWithAFUDC: ['0'],
      }),
    });

    this.landServicesDetailForm = this.fb.group({
      state: [''],
      department: [''],
      projectId: [''],
      assetLocation: [''],
      majorLocation: [''],
      streetAddress: [''],
      landUnitId: [''],
      city: [''],
      zip: [''],
      county: [''],
      town: [''],
      propertySiteId: [''],
      landPerUnit: [''],
      numberOfAcresBeingAdded: [''],
      numberOfBuildingsBeingAdded: [''],
      businessUnit: [''],
      fundingProject: [''],
      projectType: [''],
      locationType: [''],
      businessSegment: [''],
      projectClass: [''],
      projectDescription: [''],
      numberOfAcresDisposed: [''],
      bookValue: [''],
      partialDisposal: [false],
      fullDisposal: [false],
      lria: [''],
      assetId: [''],
      projectManager: [''],
      sponsor: [''],
      assetContact: [''],
      startDate: [''],
      serviceDate: [''],
      actualInServiceDate: [{ value: '', disabled: true }],
      completionDate: [''],
    });

    this.landServicesEstimateDetailsForm = this.fb.group({
      purchasePrice: [''],
      salesProceeds: [''],
      appraisal: [''],
      survey: [''],
      title: [''],
      closingCost: [''],
      legalFees: [''],
      brokerMarketingFees: [''],
      environmentalReview: [''],
      dukeLabor: [''],
      buildingDemolition: [''],
      environmentalCleanup: [''],
      total: [''],
      expenditureType: [''],
      utilityAccount: [''],
      retirementUnit: [''],
      chargeType: [''],
      transactionType: [''],
      capital: this.fb.group({
        estimateCostOfRequest: [''],
        previouslyApproved: [''],
        subtotalProjectCost: [''],
        afudcPercentage: [''],
        estimatedAfudcOfProject: [''],
        total: [''],
      }),
      om: this.fb.group({
        estimateCostOfRequest: [''],
        previouslyApproved: [''],
        total: [''],
      }),
      totalProjectCost: [''],
    });

    this.landServicesAccountingForm = this.fb.group({
      capital: this.fb.group({
        responsibilityCenter: [''],
        projectId: [''],
        activityId: [''],
        operatingUnit: [''],
        account: [''],
        processCode: [''],
        resourceType: [''],
        businessUnit: [''],
        projectChargeIndicator: ['CWIP'],
      }),
      om: this.fb.group({
        responsibilityCenter: [''],
        projectId: [''],
        activityId: [''],
        operatingUnit: [''],
        account: [''],
        processCode: [''],
        resourceType: [''],
        businessUnit: [''],
        projectChargeIndicator: ['O&M'],
      }),
    });

    this.realEstateProjectForm = this.fb.group({
      titleForm: this.realEstateTitleForm,
      detailForm: this.realEstateDetailForm,
      justificationForm: this.realEstateJustificationForm,
      uopForm: this.realEstateUopForm,
      estimateForm: this.realEstateEstimateForm,
      landServicesDetailForm: this.landServicesDetailForm,
      landServicesEstimateDetailsForm: this.landServicesEstimateDetailsForm,
      landServicesAccountingForm: this.landServicesAccountingForm,
    });

    // Sync landServicesDetailForm projectId to landServicesAccountingForm
    this.landServicesDetailForm.get('projectId').valueChanges.subscribe((projectId) => {
      this.landServicesAccountingForm
        .get('capital.projectId')
        .setValue(projectId, { emitEvent: false });
      this.landServicesAccountingForm.get('om.projectId').setValue(projectId, { emitEvent: false });
    });

    // Reset land services forms when acquisition/disposal toggles
    this.realEstateTitleForm.get('acquisition').valueChanges.subscribe(() => {
      // Do not reset while loading or when editing an existing project
      if (this.initializingLandServices || this.pId) {
        return;
      }

      if (this.realEstateTitleForm.get('disposal').value) {
        this.resetLandServicesForms();
      }
    });

    this.realEstateTitleForm.get('disposal').valueChanges.subscribe(() => {
      // Do not reset while loading or when editing an existing project
      if (this.initializingLandServices || this.pId) {
        return;
      }

      if (this.realEstateTitleForm.get('acquisition').value) {
        this.resetLandServicesForms();
      }
    });

    // Initialize baseline for brand-new forms
    this.captureBaselineFunding();

    // Call listener to look for when to move current to previous funding
    this.setupFundingSplitListener();

    this.loadEmailRecipientsFile();

    this.route.queryParams.subscribe((params) => {
      this.pId = params['pId'];
      this.changeArea = params['changeArea'] || null;
      this.pId && this.loadProjectData(this.pId);
    });
  }

  resetLandServicesForms() {
    if (this.initializingLandServices) return;
    this.landServicesDetailForm.reset();
    this.landServicesEstimateDetailsForm.reset();
    this.landServicesAccountingForm.reset();
    this.landServicesAccountingForm.get('capital.projectChargeIndicator').setValue('CWIP');
    this.landServicesAccountingForm.get('om.projectChargeIndicator').setValue('O&M');
    this.files = [];
    this.filesCopy = [];
  }

  resetFlags() {
    this.formActivityVisible = false;
    this.reviewMode = false;
    this.reviewComments = null;
    this.isReadyForDOAApproval = false;
    this.isReadyForITSpareApproval = false;
    this.readyToAddAccountingCodeBlock = false;
  }

  loadProjectData(pId: string) {
    this.resetFlags();
    this.realEstateService.getProjectData(pId).subscribe(
      (data) => {
        this.projectData = {
          ...data,
          timestamp: (data && data['timestamp']) || '',
        };
        if (!this.projectData || !this.projectData.project) return;

        const project = this.projectData.project;
        if (!this.changeArea) {
          const persistedUpdateType = this.getPersistedUpdateType(project);
          if (this.isFundingUpdateType(persistedUpdateType)) {
            this.changeArea = persistedUpdateType as 'change_in_scope' | 'estimate' | 'both';
          }
        }
        const workflow = project.workflow || [];

        if (workflow.length > 0) {
          const lastEntry = workflow[workflow.length - 1];
          this.isReadyForDOAApproval = lastEntry.isReadyForDOAApproval;
          this.isReadyForITSpareApproval = lastEntry.isReadyForITSpareApproval;
          this.readyToAddAccountingCodeBlock = lastEntry.readyToAddAccountingCodeBlock;
        }

        this.formStatus = this.projectData.project.status;
        if (this.formStatus === RREFormStatus.CREATED) {
          this.reviewMode = true;
          this.formActivityVisible = false;
        }

        if (
          [
            RREFormStatus.UNDER_REVIEW,
            RREFormStatus.APPROVED,
            RREFormStatus.REJECTED,
            RREFormStatus.IN_SERVICE,
          ].includes(this.formStatus)
        ) {
          this.reviewMode = true;
          this.formActivityVisible = true;
          this.hideActivityCTA = false;
        }

        this.doaAmounts = [0];
        if (project && project.doaApprover) {
          project.doaApprover.forEach((approver, index) => {
            this.doaAmounts[index + 1] = approver.amount;
            this.approverList[index] = approver.approver;
          });
        }

        //Prevent Land Services from clearing out on Edit
        const isLandServicesProject =
          project.titleForm && project.titleForm.realEstateDepartment === 'LAND_SERVICES';
        if (isLandServicesProject) {
          this.initializingLandServices = true;
        }

        // Real Estate Title Form
        this.realEstateTitleForm.patchValue({
          ...project.titleForm,
          fm: project.titleForm.fm === true,
          pm: project.titleForm.pm === true,
          myrp: project.titleForm.myrp === true,
        });

        setTimeout(() => {
          this.setFormData(project);
          if (isLandServicesProject) {
            this.initializingLandServices = false;
          }
        }, 100);
      },
      (error) => {
        console.error('Error loading project data:', error);
      }
    );
  }

  setFormData(project) {
    // Check if this is a Land Services project
    const isLandServicesProject =
      project.titleForm && project.titleForm.realEstateDepartment === 'LAND_SERVICES';

    if (isLandServicesProject) {
      // For Land Services: detailForm contains land services detail data
      this.projectId = project.detailForm && project.detailForm.projectId;
      this.landServicesDetailForm.patchValue({
        ...project.detailForm,
      });

      // estimateForm contains land services estimate data
      if (project.estimateForm) {
        this.landServicesEstimateDetailsForm.patchValue({
          ...project.estimateForm,
        });
      }

      // Load land services accounting form
      const accounting =
        project.accountingForm ||
        project.accounting ||
        (project.detailForm &&
          (project.detailForm.accountingForm || project.detailForm.accounting)) ||
        null;

      if (accounting) {
        // patch initial values without triggering listeners that clear dependent controls
        this.landServicesAccountingForm.patchValue(accounting, { emitEvent: false });

        // Then set dependency-driven controls in order so filters can run and child options populate.
        // Preferred order: Responsibility Center -> Operating Unit -> Account
        const cap = accounting.capital || {};
        const om = accounting.om || {};

        // CAPITAL block
        if (cap.businessUnit) {
          this.landServicesAccountingForm
            .get('capital.businessUnit')
            .setValue(cap.businessUnit, { emitEvent: true });
        }
        if (cap.responsibilityCenter) {
          // set parent and allow listeners to run so Operating Unit options are filtered
          this.landServicesAccountingForm
            .get('capital.responsibilityCenter')
            .setValue(cap.responsibilityCenter, { emitEvent: true });

          // now set operatingUnit and account (do not re-emit to avoid recursive clears)
          this.landServicesAccountingForm
            .get('capital.operatingUnit')
            .setValue(cap.operatingUnit || '', { emitEvent: false });
          this.landServicesAccountingForm
            .get('capital.account')
            .setValue(cap.account || '', { emitEvent: false });

          // optionally re-validate / notify
          this.landServicesAccountingForm
            .get('capital.operatingUnit')
            .updateValueAndValidity({ emitEvent: true });
          this.landServicesAccountingForm
            .get('capital.account')
            .updateValueAndValidity({ emitEvent: true });
        }

        // O&M block - same pattern
        if (om.businessUnit) {
          this.landServicesAccountingForm
            .get('om.businessUnit')
            .setValue(om.businessUnit, { emitEvent: true });
        }
        if (om.responsibilityCenter) {
          this.landServicesAccountingForm
            .get('om.responsibilityCenter')
            .setValue(om.responsibilityCenter, { emitEvent: true });

          this.landServicesAccountingForm
            .get('om.operatingUnit')
            .setValue(om.operatingUnit || '', { emitEvent: false });
          this.landServicesAccountingForm
            .get('om.account')
            .setValue(om.account || '', { emitEvent: false });

          this.landServicesAccountingForm
            .get('om.operatingUnit')
            .updateValueAndValidity({ emitEvent: true });
          this.landServicesAccountingForm
            .get('om.account')
            .updateValueAndValidity({ emitEvent: true });
        }
      } else {
        console.warn(
          'No accounting block found in loaded project (checked accountingForm, accounting, detailForm.accountingForm, detailForm.accounting).'
        );
      }

      // Rehydrate attachments (if saved)
      if (project.attachments && Array.isArray(project.attachments)) {
        this.filesCopy = project.attachments.map((a) => {
          const storagePath =
            a.storageFilePath ||
            (project.detailForm && project.detailForm.department && project.detailForm.projectId
              ? `${project.detailForm.department}/${project.detailForm.projectId}/${a.fileName}`
              : a.fileName);
          return {
            fileName: a.fileName,
            contentType: a.contentType,
            uploadedBy: a.uploadedBy || '',
            uploadedDate: a.uploadedDate || '',
            storageFilePath: storagePath,
          };
        });
        this.files = this.filesCopy.map(
          (f) => new UploadFile(f.uploadedBy, f.uploadedDate, f.fileName, f.contentType)
        );
      } else {
        this.files = [];
        this.filesCopy = [];
      }
    } else {
      // For Real Estate: load standard real estate forms
      this.projectId = project.detailForm && project.detailForm.projectId;
      this.realEstateDetailForm.patchValue({
        ...project.detailForm,
        actualInServiceDate: (project.detailForm && project.detailForm.actualInServiceDate) || '',
      });

      // Real Estate Justification Form
      if (project.justificationForm) {
        this.realEstateJustificationForm.patchValue({
          projectJustification: project.justificationForm.projectJustification,
        });
        const justificationReasonFormArray = this.realEstateJustificationForm.get(
          'projectReason'
        ) as FormArray;

        if (project.justificationForm.projectReason) {
          for (let i = 0; i < project.justificationForm.projectReason.length; i++) {
            justificationReasonFormArray.push(
              this.fb.control(project.justificationForm.projectReason[i])
            );
          }
        }
      }

      // Real Estate UOP Form
      if (project.uopForm) {
        const uopDetailsFormArray = this.realEstateUopForm.get('uopDetails') as FormArray;
        uopDetailsFormArray.clear(); // Clear existing entries

        if (project.uopForm.uopDetails) {
          for (let i = 0; i < project.uopForm.uopDetails.length; i++) {
            uopDetailsFormArray.push(this.fb.group(project.uopForm.uopDetails[i]));
            uopDetailsFormArray.disable();
          }
        }
      }

      // Real Estate Estimate Form
      if (project.estimateForm) {
        this.realEstateEstimateForm.patchValue({
          ...project.estimateForm,
        });

        const estimatesFormArray = this.realEstateEstimateForm.get('estimates') as FormArray;
        estimatesFormArray.clear(); // Clear existing entries

        if (project.estimateForm.estimates) {
          for (let i = 0; i < project.estimateForm.estimates.length; i++) {
            estimatesFormArray.push(this.fb.group(project.estimateForm.estimates[i]));
            estimatesFormArray.disable();
          }
        }
      }

      setTimeout(() => {
        if (!this.reviewMode) {
          // Edit mode: preserve submitted baseline for update flows.
          // For non-update flows, keep legacy reset behavior.
          if (!this.isFundingUpdateFlow()) {
            this.clearPreviousFunding();
          }
          this.captureBaselineFunding();
          this.applyPreviousFundingSplitForRequest();
        } else {
          // Review/approval mode: keep persisted previous/current exactly as submitted
          this.normalizeExpenditureTotals();
          this.captureBaselineFunding();
        }
      }, 0);

      // Rehydrate attachments for Real Estate projects
      if (project.attachments && Array.isArray(project.attachments)) {
        this.filesCopy = project.attachments.map((a) => {
          const storagePath =
            a.storageFilePath ||
            (project.detailForm && project.detailForm.department && project.detailForm.projectId
              ? `${project.detailForm.department}/${project.detailForm.projectId}/${a.fileName}`
              : a.fileName);
          return {
            fileName: a.fileName,
            contentType: a.contentType,
            uploadedBy: a.uploadedBy || '',
            uploadedDate: a.uploadedDate || '',
            storageFilePath: storagePath,
          };
        });
        this.files = this.filesCopy.map(
          (f) => new UploadFile(f.uploadedBy, f.uploadedDate, f.fileName, f.contentType)
        );
      } else {
        this.files = [];
        this.filesCopy = [];
      }
    }

    // Real Estate Workflow
    this.formActivity =
      project.workflow.map((activity) => {
        return {
          ...activity,
          date: parseDateTime(new Date(activity.date)),
        };
      }) || [];
    //``;

    this.realEstateWorkflow = project.workflow || [];
    this.formOwner = (project.workflow && project.workflow[0].assignedBy) || null;
    this.currentAssignment =
      (project.workflow &&
        project.workflow[project.workflow && project.workflow.length - 1].assignedTo) ||
      null;
    this.applyChangeAreaRestrictions();
  }

  applyChangeAreaRestrictions() {
    const isRealEstate =
      this.realEstateTitleForm.get('realEstateDepartment').value === 'REAL_ESTATE';

    if (isRealEstate && this.changeArea && !this.reviewMode) {
      if (this.changeArea === 'change_in_scope') {
        this.disableEstimateSection = true;
      } else if (this.changeArea === 'estimate') {
        this.disableDetailSection = true;
      }
      // For 'both', no restrictions apply
    }
  }

  // Attachments section methods - copied from IT SPAR with minor modifications noted
  onFileSelected(event) {
    for (let i = 0; i < event.target.files.length; i++) {
      this.uploadFile(event.target.files[i]);
    }
  }

  uploadFile(file: File) {
    const userData = JSON.parse(sessionStorage.getItem('userInfo'));
    const stringArray = userData.account.name.split(',');
    const lastName = stringArray[0];
    const firstName = stringArray[1] ? stringArray[1].substring(1) : '';
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = today.getMonth() + 1;
    const dd = today.getDate();
    const ddstring = dd < 10 ? `0${dd}` : dd.toString();
    const mmstring = mm < 10 ? `0${mm}` : mm.toString();
    const formattedToday = `${mmstring}/${ddstring}/${yyyy}`;

    const isLandServices =
      this.realEstateTitleForm.get('realEstateDepartment').value === 'LAND_SERVICES';
    const dept = isLandServices
      ? this.landServicesDetailForm.get('department').value
      : this.realEstateDetailForm.get('department').value;
    const pid = isLandServices
      ? this.landServicesDetailForm.get('projectId').value
      : this.realEstateDetailForm.get('projectId').value;

    const storagePath = `${dept}/${pid}/${file.name}`;

    const data = {
      fileName: storagePath,
      contentType: file.type,
      method: 'put_object',
    };

    this.dataService.getPresignedURL(data).subscribe(
      (result) => {
        const presignedURL = JSON.parse(result as string).presigned_url;
        this.httpNoInterceptors
          .put(presignedURL, file, { headers: { 'Content-Type': file.type } })
          .subscribe(
            () => {
              // UI list for display (UploadFile stays the same)
              this.files.push(
                new UploadFile(`${firstName} ${lastName}`, formattedToday, file.name, file.type)
              );

              // Persistable descriptor for payloads and rehydration (filesCopy)
              this.filesCopy.push({
                fileName: file.name,
                contentType: file.type,
                uploadedBy: `${firstName} ${lastName}`,
                uploadedDate: formattedToday,
                storageFilePath: storagePath,
              });
            },
            (error) => {
              let errorType = '';
              let errorDescription = '';
              if (error.status === 0) {
                errorType = 'Upload Blocked';
                errorDescription =
                  'File upload was blocked. Your network security policy may not allow this file type to be uploaded. Please try uploading a PDF instead.';
              } else {
                if (error && error.error && error.error.message) {
                  errorDescription = errorDescription.concat(
                    'Upload File to Presigned URL Error - ',
                    error.error.message
                  );
                } else {
                  errorDescription = errorDescription.concat('Upload File to Presigned URL Error');
                }
                errorType = errorType.concat(error.status, ' ', error.statusText);
              }
              const dataStructure = {
                errorType: errorType,
                errorDescription: errorDescription,
              };
              this.dialog.open(ErrorDialogComponent, {
                data: dataStructure,
                disableClose: true,
              });
            }
          );
      },
      (error) => {
        let errorType = '';
        let errorDescription = '';
        if (error && error.error && error.error.message) {
          errorDescription = errorDescription.concat(
            'Get Presigned URL Error - ',
            error.error.message
          );
        } else {
          errorDescription = errorDescription.concat('Get Presigned URL Error');
        }
        errorType = errorType.concat(error.status, ' ', error.statusText);
        const dataStructure = {
          errorType: errorType,
          errorDescription: errorDescription,
        };
        this.dialog.open(ErrorDialogComponent, {
          data: dataStructure,
          disableClose: true,
        });
      }
    );
  }

  onDrop(event: any) {
    event.preventDefault();
    for (let i = 0; i < event.dataTransfer.files.length; i++) {
      this.uploadFile(event.dataTransfer.files[i]);
    }
  }

  onDragOver(event: any) {
    event.preventDefault();
  }

  deleteFile(index: number) {
    // Remove from persisted descriptor list
    this.filesCopy.splice(index, 1);
    // Also remove from UI UploadFile list to keep UI in sync
    if (this.files && this.files.length > index) {
      this.files.splice(index, 1);
    }
  }

  downloadFile(index: number) {
    // Determine project type and fallback dept/pid if needed
    const isLandServices =
      this.realEstateTitleForm.get('realEstateDepartment').value === 'LAND_SERVICES';
    const dept = isLandServices
      ? this.landServicesDetailForm.get('department').value
      : this.realEstateDetailForm.get('department').value;
    const pid = isLandServices
      ? this.landServicesDetailForm.get('projectId').value
      : this.realEstateDetailForm.get('projectId').value;

    // Prefer persisted descriptor (filesCopy) which contains storageFilePath
    const descriptor = this.filesCopy && this.filesCopy[index];
    if (!descriptor) {
      // Nothing to download
      return;
    }

    const storedPath = descriptor.storageFilePath || `${dept}/${pid}/${descriptor.fileName}`;
    const contentType = descriptor.contentType;

    const data = {
      fileName: storedPath,
      contentType: contentType,
      method: 'get_object',
    };

    this.dataService.getPresignedURL(data).subscribe(
      (result) => {
        const presignedUrl = JSON.parse(result as string).presigned_url;
        this.http
          .get(presignedUrl, {
            responseType: 'blob',
          })
          .subscribe((blob) => {
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = descriptor.fileName;
            a.click();
            URL.revokeObjectURL(url);
          });
      },
      (error) => {
        let errorType = '';
        let errorDescription = '';
        if (error && error.error && error.error.message) {
          errorDescription = errorDescription.concat(
            'Get Presigned URL Error - ',
            error.error.message
          );
        } else {
          errorDescription = errorDescription.concat('Get Presigned URL Error');
        }
        errorType = errorType.concat(error.status, ' ', error.statusText);
        const dataStructure = {
          errorType: errorType,
          errorDescription: errorDescription,
        };
        this.dialog.open(ErrorDialogComponent, {
          data: dataStructure,
          disableClose: true,
        });
      }
    );
  }

  reviewForm() {
    //Reset previous funding if current values haven't changed
    this.normalizeExpenditureTotals();
    this.applyPreviousFundingSplitForRequest();
    const effectiveUpdateType = this.getEffectiveUpdateType();
    const doaApprover = this.approverList.map((approver, index) => {
      return {
        approver: approver,
        amount: this.doaAmounts[index + 1],
      };
    });
    const formValue = this.realEstateProjectForm.value;
    const isLandServicesProject =
      this.realEstateTitleForm.get('realEstateDepartment').value === 'LAND_SERVICES';

    const payload: any = {
      id: this.pId ? this.pId : '',
      titleForm: this.realEstateTitleForm.getRawValue(),
      doaApprover: doaApprover,
      workflow:
        this.projectData && this.projectData.project && this.projectData.project.workflow
          ? this.projectData.project.workflow
          : [],
      attachments: this.filesCopy || [],
    };

    let projectManager: any;

    if (isLandServicesProject) {
      payload.detailForm = this.landServicesDetailForm.getRawValue();
      payload.estimateForm = this.landServicesEstimateDetailsForm.getRawValue();
      payload.accountingForm = this.landServicesAccountingForm.getRawValue();
      projectManager = this.landServicesDetailForm.get('projectManager').value;
    } else {
      payload.detailForm = this.realEstateDetailForm.getRawValue();
      payload.estimateForm = this.realEstateEstimateForm.getRawValue();
      payload.justificationForm = this.realEstateJustificationForm.getRawValue();
      payload.uopForm = this.realEstateUopForm.getRawValue();
      projectManager = this.realEstateDetailForm.get('projectManager').value;
    }

    // **NEW: Map changeArea to email-friendly label**
    let updateTypeLabel = '';
    if (effectiveUpdateType === 'estimate') {
      updateTypeLabel = 'Estimate update';
    } else if (effectiveUpdateType === 'change_in_scope') {
      updateTypeLabel = 'Change in Scope';
    } else if (effectiveUpdateType === 'both') {
      updateTypeLabel = 'Change in Scope & Estimate';
    }

    const workFlowPayload = {
      assignedBy: projectManager,
      assignedTo: this.approverList[0],
      date: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
      comments: '',
      title: 'Sent to DOA from PM',
      status: RREFormStatus.UNDER_REVIEW,
      isReadyForDOAApproval: true,
      // Include update type for email template**
      updateType: effectiveUpdateType,
      updateTypeLabel: updateTypeLabel,
    };

    payload.workflow.push(workFlowPayload);

    // Carry forward the existing accountingCodeBlock so it is never lost on PM resubmit
    if (
      this.projectData &&
      this.projectData.project &&
      this.projectData.project.accountingCodeBlock
    ) {
      payload['accountingCodeBlock'] = this.projectData.project.accountingCodeBlock;
    }

    if (this.projectData && this.projectData['timestamp']) {
      payload['timestamp'] = this.projectData['timestamp'];
    }

    payload['status'] = RREFormStatus.UNDER_REVIEW;
    payload['version'] = 1;

    // Set createdBy on first submission, preserve existing value on resubmissions
    if (this.projectData && this.projectData.project['createdBy']) {
      payload['createdBy'] = this.projectData.project['createdBy'];
    } else {
      payload['createdBy'] = this.userName;
    }

    if (this.projectData && this.projectData.project && this.projectData.project['version']) {
      payload['version'] = this.projectData.project['version'] + 1;
    }

    // Ensure backend/email lambda receives machine-readable update type
    if (effectiveUpdateType) {
      payload['updateType'] = effectiveUpdateType; // 'estimate' | 'change_in_scope' | 'both'
    }

    // Pass updateTypeLabel to the payload for backend email service
    payload['updateTypeLabel'] = updateTypeLabel;

    this.realEstateService.saveAsDraftRealEstateData(payload).subscribe(
      (response) => {
        this.showToaster('success', 'Project sent for Review.', 6000);
        this.router.navigate(['/real-estate-project'], {
          queryParams: {
            pId: response['project_id'],
            ...(this.isFundingUpdateType(effectiveUpdateType)
              ? { changeArea: effectiveUpdateType }
              : {}),
          },
        });
        this.loadProjectData(response['project_id']);
        this.reviewMode = true;
        setTimeout(() => {
          const el = document.getElementById('pageTitle');

          if (el) {
            el.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
        });
      },
      (error) => {
        console.error('Submission failed:', error.error);
        this.dialog.open(RealEstateFormErrorDialogComponent, {
          width: '800px',
          disableClose: true,
          data: {
            error: error.error,
          },
        });
      }
    );
  }

  exitReviewMode() {
    if (!this.isFormApproved() || this.isFundingUpdateFlow()) {
      this.reviewMode = false;
      this.applyChangeAreaRestrictions();

      // reset split baseline for new edit cycle
      if (!this.isFundingUpdateFlow()) {
        this.clearPreviousFunding();
      }
      this.captureBaselineFunding();
      this.applyPreviousFundingSplitForRequest();
    }
  }

  saveDraft() {
    this.normalizeExpenditureTotals();
    this.applyPreviousFundingSplitForRequest();
    const effectiveUpdateType = this.getEffectiveUpdateType();
    const doaApprover = this.approverList.map((approver, index) => {
      return {
        approver: approver,
        amount: this.doaAmounts[index + 1],
      };
    });

    const formValue = this.realEstateProjectForm.value;
    const isLandServicesProject =
      this.realEstateTitleForm.get('realEstateDepartment').value === 'LAND_SERVICES';

    let payload = {};

    if (isLandServicesProject) {
      payload = {
        titleForm: this.realEstateTitleForm.getRawValue(),
        detailForm: this.landServicesDetailForm.getRawValue(),
        estimateForm: this.landServicesEstimateDetailsForm.getRawValue(),
        accountingForm: this.landServicesAccountingForm.getRawValue(),
        doaApprover: doaApprover,
        attachments: this.filesCopy || [],
        workflow: [
          {
            assignedBy: this.landServicesDetailForm.get('projectManager').value,
            assignedTo: this.landServicesDetailForm.get('projectManager').value,
            date: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
            comments: '',
            title: 'Draft',
            status: RREFormStatus.CREATED,
          },
        ],
      };
    } else {
      payload = {
        titleForm: this.realEstateTitleForm.getRawValue(),
        detailForm: this.realEstateDetailForm.getRawValue(),
        estimateForm: this.realEstateEstimateForm.getRawValue(),
        justificationForm: this.realEstateJustificationForm.getRawValue(),
        uopForm: this.realEstateUopForm.getRawValue(),
        doaApprover: doaApprover,
        attachments: this.filesCopy || [],
        workflow: [
          {
            assignedBy: this.realEstateDetailForm.get('projectManager').value,
            assignedTo: this.realEstateDetailForm.get('projectManager').value,
            date: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
            comments: '',
            title: 'Draft',
            status: RREFormStatus.CREATED,
          },
        ],
      };
    }

    if (this.projectData && this.projectData['timestamp']) {
      payload['timestamp'] = this.projectData['timestamp'];
    }
    if (this.pId) {
      payload['id'] = this.pId;
    }

    payload['status'] = RREFormStatus.CREATED;
    payload['version'] = 1;

    // Set createdBy on first creation, preserve existing value on subsequent saves
    if (this.projectData && this.projectData.project && this.projectData.project['createdBy']) {
      payload['createdBy'] = this.projectData.project['createdBy'];
    } else {
      payload['createdBy'] = this.userName;
    }

    if (this.projectData && this.projectData.project && this.projectData.project['version']) {
      payload['version'] = this.projectData.project['version'] + 1;
    }

    if (effectiveUpdateType) {
      payload['updateType'] = effectiveUpdateType;
    }

    this.realEstateService.saveAsDraftRealEstateData(payload).subscribe(
      (response) => {
        this.showToaster('success', 'Draft created successfully.', 6000);
        if (this.pId) {
          this.loadProjectData(this.pId);
        } else {
          this.router.navigate(['/real-estate-project'], {
            queryParams: {
              pId: response['project_id'],
              ...(this.isFundingUpdateType(effectiveUpdateType)
                ? { changeArea: effectiveUpdateType }
                : {}),
            },
          });
        }
      },
      (error) => {
        console.error('Submission failed:', error.error);
        this.dialog.open(RealEstateFormErrorDialogComponent, {
          width: '800px',
          disableClose: true,
          data: {
            error: error.error,
          },
        });
      }
    );
  }

  launchAssignmentDialog() {
    if (this.isCurrentApprover() && !this.readyToAddAccountingCodeBlock) {
      let dataStructure;
      dataStructure = {
        formOwner: this.realEstateWorkflow && this.realEstateWorkflow[0].assignedBy,
        currentAssignment: this.realEstateWorkflow && this.realEstateWorkflow[0].assignedTo,
      };
      const dialogRef = this.dialog.open(RealEstateAssignmentDialogComponent, {
        data: dataStructure,
        disableClose: true,
      });

      dialogRef.componentInstance.onYes.subscribe((result) => {
        this.projectData.project.workflow[this.projectData.project.workflow.length - 1] = {
          ...this.projectData.project.workflow[this.projectData.project.workflow.length - 1],
          assignedTo: result.currentAssignment,
        };

        if (this.projectData && this.projectData['timestamp']) {
          this.projectData['timestamp'] = this.projectData['timestamp'];
        }

        const payload = {
          ...this.projectData.project,
          id: this.pId ? this.pId : '',
          timestamp: this.projectData['timestamp'],
        };

        payload['version'] = 1;

        if (this.projectData && this.projectData.project && this.projectData.project['version']) {
          payload['version'] = this.projectData.project['version'] + 1;
        }

        this.realEstateService.saveAsDraftRealEstateData(payload).subscribe(
          (response) => {
            this.showToaster('success', 'Changes Saved.', 6000);
            this.loadProjectData(this.pId);
          },
          (error) => {
            console.error('Submission failed:', error.error);
            this.dialog.open(RealEstateFormErrorDialogComponent, {
              width: '800px',
              disableClose: true,
              data: {
                error: error.error,
              },
            });
          }
        );
      });
    }
  }

  getUserDetails(searchInput: string) {
    this.realEstateService
      .userSearch(searchInput)
      .then((users) => {
        this.formOwnerFullDetails = users.find((user) => user.mail === this.userEmail);
      })
      .catch((error) => {
        console.error('User search error:', error);
      });
  }

  loadEmailRecipientsFile() {
    this.http.get('assets/email_recipients.json').subscribe(
      (result) => {
        this.emailRecipients = result as any;
      },
      (error) => {
        console.error('Load Email Recipients Error:', error);
        this.dialog.open(ErrorDialogComponent, {
          data: {
            errorType: error.status + ' ' + error.statusText,
            errorDescription:
              error.error && error.error.message
                ? 'Load Email Recipients Error - ' + error.error.message
                : 'Load Email Recipients Error',
          },
          disableClose: true,
        });
      }
    );
  }

  showToaster(type, msg, dur) {
    const data = new DeSnackbarData();
    data.type = type;
    data.text = msg;
    data.duration = dur;
    data.showCloseIcon = true;
    this.snackService.show(data);
  }

  toggleFormActivity() {
    this.formActivityVisible = !this.formActivityVisible;
  }

  isCurrentApprover() {
    return (
      this.userEmail &&
      this.currentAssignment &&
      this.currentAssignment.mail &&
      this.userEmail.toLowerCase() === this.currentAssignment.mail.toLowerCase()
    );
  }

  isFormApproved() {
    return this.formStatus === RREFormStatus.APPROVED;
  }

  approveForm({
    isReject = false,
    accountingCodeBlock = null,
  }: {
    isReject?: boolean;
    accountingCodeBlock?: any;
  }) {
    const dataStructure = {
      reviewComments: this.reviewComments,
      isReject: isReject,
    };
    let height;
    if (!this.reviewComments) {
      height = '14.5rem';
    }

    const dialogRef = this.dialog.open(FormApprovalDialogComponent, {
      data: dataStructure,
      disableClose: true,
      width: '50.375rem',
      height: height,
    });
    dialogRef.componentInstance.onYes.subscribe(() => {
      const isAccountingSubmission = this.readyToAddAccountingCodeBlock && !isReject;
      let workflowPayload = {};
      const formOwner = this.projectData.project.workflow[0].assignedBy;

      // Get updateType from changeArea or first workflow entry that has it
      let originalUpdateType = null;
      if (this.changeArea) {
        originalUpdateType = this.changeArea;
      } else if (
        this.projectData &&
        this.projectData.project &&
        this.projectData.project.workflow
      ) {
        const workflowWithUpdateType = this.projectData.project.workflow.find(function (w) {
          return w.updateType;
        });
        if (workflowWithUpdateType) {
          originalUpdateType = workflowWithUpdateType.updateType;
        }
      }
      if (
        !originalUpdateType &&
        this.projectData &&
        this.projectData.project &&
        this.projectData.project.updateType
      ) {
        originalUpdateType = this.projectData.project.updateType;
      }

      const itSparEmail = {
        displayName: 'AS-Initiate-SPAR',
        givenName: null,
        surname: null,
        mail: this.emailRecipients.groups.sparInitiationGroup.email,
        mailNickname: 'AS-Initiate-SPAR',
        jobTitle: null,
      };
      if (this.isReadyForDOAApproval) {
        workflowPayload = {
          assignedBy: this.currentAssignment,
          assignedTo: isReject ? formOwner : itSparEmail,
          comments: dataStructure.reviewComments || '',
          title: isReject ? 'Rejected by DOA' : 'Approved by DOA',
          status: isReject ? RREFormStatus.REJECTED : RREFormStatus.UNDER_REVIEW,
          date: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
          isReadyForITSpareApproval: !isReject,
          updateType: originalUpdateType,
        };
      }

      if (this.isReadyForITSpareApproval) {
        workflowPayload = {
          assignedBy: this.currentAssignment,
          assignedTo: isReject ? formOwner : this.currentAssignment,
          comments: dataStructure.reviewComments || '',
          title: isReject ? 'Rejected by IT SPAC' : 'Approved by IT SPAC',
          status: isReject ? RREFormStatus.REJECTED : RREFormStatus.UNDER_REVIEW,
          date: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
          isReadyForITSpareApproval: !isReject,
          readyToAddAccountingCodeBlock: !isReject,
          updateType: originalUpdateType,
        };
      }

      if (this.readyToAddAccountingCodeBlock) {
        workflowPayload = {
          assignedBy: this.currentAssignment,
          assignedTo: formOwner,
          comments: dataStructure.reviewComments || '',
          title: 'Accounting code block submitted by IT SPAC',
          status: RREFormStatus.APPROVED,
          date: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
          updateType: originalUpdateType,
        };
      }

      this.projectData.project.workflow.push(workflowPayload);

      if (this.projectData && this.projectData['timestamp']) {
        this.projectData['timestamp'] = this.projectData['timestamp'];
      }

      const payload = {
        ...this.projectData.project,
        id: this.pId ? this.pId : '',
        timestamp: this.projectData['timestamp'],
      };

      // Persist attachments when approving/saving
      payload.attachments = this.filesCopy || [];

      // Ensure current form values are merged into the payload so updated fields persist
      const isLandServicesProject =
        this.realEstateTitleForm.get('realEstateDepartment').value === 'LAND_SERVICES';

      if (isLandServicesProject) {
        payload.detailForm = this.landServicesDetailForm.getRawValue();
        payload.estimateForm = this.landServicesEstimateDetailsForm.getRawValue();
        payload.accountingForm = this.landServicesAccountingForm.getRawValue();
      } else {
        payload.detailForm = this.realEstateDetailForm.getRawValue();
        payload.estimateForm = this.realEstateEstimateForm.getRawValue();
        payload.justificationForm = this.realEstateJustificationForm.getRawValue();
        payload.uopForm = this.realEstateUopForm.getRawValue();
      }

      payload['version'] = 1;

      if (this.isReadyForDOAApproval) {
        payload['status'] = isReject ? RREFormStatus.REJECTED : RREFormStatus.UNDER_REVIEW;
      }

      if (this.isReadyForITSpareApproval) {
        payload['status'] = isReject ? RREFormStatus.REJECTED : RREFormStatus.UNDER_REVIEW;
      }

      if (this.readyToAddAccountingCodeBlock) {
        payload['accountingCodeBlock'] = accountingCodeBlock || {};
        payload['status'] = RREFormStatus.APPROVED;
      }

      if (this.projectData && this.projectData.project && this.projectData.project['version']) {
        payload['version'] = this.projectData.project['version'] + 1;
      }

      console.error('payload :>> ', payload);
      this.realEstateService.saveAsDraftRealEstateData(payload).subscribe(
        (response) => {
          this.showToaster('success', isReject ? 'Form is Rejected.' : 'Form is Approved.', 6000);
          // Prevent stale UI right after accounting save by updating local state first.
          if (isAccountingSubmission) {
            this.projectData = {
              ...this.projectData,
              project: {
                ...this.projectData.project,
                accountingCodeBlock: payload['accountingCodeBlock'] || {},
                status: payload['status'],
                workflow: payload['workflow'],
              },
            };
            this.formStatus = RREFormStatus.APPROVED;
            this.readyToAddAccountingCodeBlock = false;
            this.isReadyForDOAApproval = false;
            this.isReadyForITSpareApproval = false;

            const responseProjectId = response && response['project_id'];
            setTimeout(() => {
              this.loadProjectData(responseProjectId || this.pId);
            }, 1000);
            return;
          }

          const responseProjectId = response && response['project_id'];
          // Immediately clear approval flags so the Approve button disappears
          // before loadProjectData completes, preventing duplicate submissions.
          this.isReadyForDOAApproval = false;
          this.isReadyForITSpareApproval = false;
          this.loadProjectData(responseProjectId || this.pId);
        },
        (error) => {
          console.error('Submission failed:', error.error);
          this.dialog.open(RealEstateFormErrorDialogComponent, {
            width: '800px',
            disableClose: true,
            data: {
              error: error.error,
            },
          });
        }
      );
    });
  }

  openDeleteConfirmationDialog(): void {
    const dialogRef = this.dialog.open(ConfirmDeleteFormDialogComponent, {
      width: '400px',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'delete') {
        this.deleteForm();
      }
    });
  }

  deleteForm(): void {
    console.log('Delete Form clicked');

    if (!this.pId) {
      console.error('No project ID found for deletion');
      return;
    }

    const payload = {
      id: this.pId,
      status: RREFormStatus.DELETED,
      timestamp:
        this.projectData && this.projectData['timestamp']
          ? this.projectData['timestamp']
          : undefined,
    };

    if (this.projectData && this.projectData.project && this.projectData.project['version']) {
      payload['version'] = this.projectData.project['version'] + 1;
    }

    this.realEstateService.saveAsDraftRealEstateData(payload).subscribe(
      (response) => {
        this.showToaster('success', 'Form deleted successfully.', 6000);
        this.router.navigate(['/real-estate-project-dashboard']);
      },
      (error) => {
        console.error('Deletion failed:', error);
        let errorMessage = 'An error occurred while deleting the form.';

        if (error && error.error && error.error.message) {
          errorMessage = error.error.message;
        } else if (error && error.message) {
          errorMessage = error.message;
        }

        this.dialog.open(RealEstateFormErrorDialogComponent, {
          width: '800px',
          disableClose: true,
          data: {
            error: errorMessage,
          },
        });
      }
    );
  }

  private toNumber(value: any): number {
    const n = parseFloat(value);
    return isNaN(n) ? 0 : n;
  }

  private clearPreviousFunding() {
    const expGroup = this.realEstateEstimateForm.get('expenditureSummary') as FormGroup;
    if (!expGroup) return;

    expGroup.patchValue(
      {
        previousEstCapCost: '0',
        previousEstAFUDC: '0',
        previousTotalCapital: '0',
        previousEstOM: '0',
        previousTotalNoAFUDC: '0',
        previousTotalWithAFUDC: '0',
      },
      { emitEvent: false }
    );
  }

  /**
   * If changed:
   *   previous = baseline current
   *   current  = new total - baseline current (request delta)
   * If unchanged:
   *   previous = 0
   */
  private fundingSplitInProgress = false;
  private fundingSplitListenerBound = false;

  private getPersistedUpdateType(project: any): string | null {
    if (!project) return null;
    if (project.updateType) return project.updateType;
    if (project.detailForm && project.detailForm.updateType) {
      return project.detailForm.updateType;
    }
    if (project.workflow && project.workflow.length > 0) {
      const workflowWithUpdateType = project.workflow.find((w) => {
        return w && w.updateType;
      });
      if (workflowWithUpdateType) {
        return workflowWithUpdateType.updateType;
      }
    }
    return null;
  }

  private getEffectiveUpdateType(): string | null {
    if (this.changeArea) return this.changeArea;
    if (this.projectData && this.projectData.project) {
      return this.getPersistedUpdateType(this.projectData.project);
    }
    return null;
  }

  private isFundingUpdateType(updateType: string | null): boolean {
    return updateType === 'estimate' || updateType === 'change_in_scope' || updateType === 'both';
  }

  private isFundingUpdateFlow(): boolean {
    return this.isFundingUpdateType(this.getEffectiveUpdateType());
  }

  private applyPreviousFundingSplitForRequest() {
    const expGroup = this.realEstateEstimateForm.get('expenditureSummary') as FormGroup;
    if (!expGroup || !this.baselineCurrentFunding) return;
    if (this.fundingSplitInProgress) return;

    this.normalizeExpenditureTotals();

    this.fundingSplitInProgress = true;

    try {
      const isUpdateFlow = this.isFundingUpdateFlow();
      let hasAnyChange = false;
      const patch: any = {};

      // Pass 1: compute full values and detect whether anything changed vs baseline
      for (let i = 0; i < this.fundingMappings.length; i++) {
        const m = this.fundingMappings[i];
        if (m.total === 'totalEstAFUDC') {
          continue;
        }
        const totalControl = expGroup.get(m.total);
        const totalVal = this.toNumber(totalControl ? totalControl.value : 0);
        const baselineVal = this.toNumber(this.baselineCurrentFunding[m.current]);

        if (totalVal !== baselineVal) {
          hasAnyChange = true;
          break;
        }
      }

      // In explicit update flows, always carry baseline into previous funding.
      // Outside update flows (e.g., initial drafts), keep legacy behavior.
      if (isUpdateFlow) {
        hasAnyChange = true;
      }

      // Pass 2: split into previous/current while preserving full total
      for (let i = 0; i < this.fundingMappings.length; i++) {
        const m = this.fundingMappings[i];
        const totalControl = expGroup.get(m.total);
        const totalVal = this.toNumber(totalControl ? totalControl.value : 0);
        const baselineVal = this.toNumber(this.baselineCurrentFunding[m.current]);

        // If there's a change, previous = baseline, current = delta.
        // If no change, previous = 0, current = full total.
        patch[m.previous] = hasAnyChange ? baselineVal : 0;
        patch[m.current] = hasAnyChange ? totalVal - baselineVal : totalVal;
        patch[m.total] = totalVal;
      }

      expGroup.patchValue(patch, { emitEvent: false });
    } finally {
      this.fundingSplitInProgress = false;
    }
  }

  /**
   * Capture current funding as baseline for change detection.
   */
  private captureBaselineFunding() {
    const expGroup = this.realEstateEstimateForm.get('expenditureSummary') as FormGroup;
    if (!expGroup) return;

    const isUpdateFlow = this.isFundingUpdateFlow();
    const hasPersistedPreviousFunding = this.fundingMappings.some((m) => {
      const previousCtrl = expGroup.get(m.previous);
      return this.toNumber(previousCtrl ? previousCtrl.value : 0) !== 0;
    });

    const readFull = (currentKey: string, totalKey: string): number => {
      const totalVal = this.toNumber(expGroup.get(totalKey) ? expGroup.get(totalKey).value : 0);
      const currentVal = this.toNumber(
        expGroup.get(currentKey) ? expGroup.get(currentKey).value : 0
      );
      return totalVal !== 0 ? totalVal : currentVal;
    };

    const readSubmittedBaseline = (
      currentKey: string,
      previousKey: string,
      totalKey: string
    ): number => {
      if (isUpdateFlow && hasPersistedPreviousFunding) {
        const previousVal = this.toNumber(
          expGroup.get(previousKey) ? expGroup.get(previousKey).value : 0
        );
        return previousVal;
      }
      return readFull(currentKey, totalKey);
    };

    // ...existing code...
    const stateCtrl = this.realEstateDetailForm.get('state');
    const isFlorida = stateCtrl && stateCtrl.value === 'FL';

    this.baselineCurrentFunding = {
      currentEstCapCost: readSubmittedBaseline(
        'currentEstCapCost',
        'previousEstCapCost',
        'totalEstCapCost'
      ),
      currentEstAFUDC: isFlorida
        ? 0
        : readSubmittedBaseline('currentEstAFUDC', 'previousEstAFUDC', 'totalEstAFUDC'),
      currentTotalCapital: readSubmittedBaseline(
        'currentTotalCapital',
        'previousTotalCapital',
        'totalTotalCapital'
      ),
      currentEstOM: readSubmittedBaseline('currentEstOM', 'previousEstOM', 'totalEstOM'),
      currentTotalNoAFUDC: readSubmittedBaseline(
        'currentTotalNoAFUDC',
        'previousTotalNoAFUDC',
        'totalTotalNoAFUDC'
      ),
      currentTotalWithAFUDC: readSubmittedBaseline(
        'currentTotalWithAFUDC',
        'previousTotalWithAFUDC',
        'totalTotalWithAFUDC'
      ),
    };

    this.baselinePreviousFunding = {
      previousEstCapCost: 0,
      previousEstAFUDC: 0,
      previousTotalCapital: 0,
      previousEstOM: 0,
      previousTotalNoAFUDC: 0,
      previousTotalWithAFUDC: 0,
    };
  }

  private setupFundingSplitListener() {
    if (this.fundingSplitListenerBound) return;

    // LISTEN TO THE ENTIRE ESTIMATE FORM, NOT A SUB-GROUP
    this.realEstateEstimateForm.valueChanges.subscribe(() => {
      //Do not run the split logic if the form is in review mode.
      if (this.reviewMode) return;
      if (!this.baselineCurrentFunding) return;
      if (this.fundingSplitInProgress) return;

      // Use setTimeout to ensure this runs after the child component's calculations are complete
      setTimeout(() => {
        this.applyPreviousFundingSplitForRequest();
      }, 0);
    });

    this.fundingSplitListenerBound = true;
  }

  private normalizeExpenditureTotals() {
    const expGroup = this.realEstateEstimateForm.get('expenditureSummary') as FormGroup;
    if (!expGroup) return;

    const totalEstCapCost = this.toNumber(expGroup.get('totalEstCapCost').value);
    const totalEstOM = this.toNumber(expGroup.get('totalEstOM').value);
    const totalEstAFUDC = this.toNumber(expGroup.get('totalEstAFUDC').value);

    expGroup.patchValue(
      {
        totalTotalCapital: totalEstCapCost + totalEstAFUDC,
        totalTotalNoAFUDC: totalEstCapCost + totalEstOM,
        totalTotalWithAFUDC: totalEstCapCost + totalEstOM + totalEstAFUDC,
      },
      { emitEvent: false }
    );
  }
}
